import type { ComponentType } from 'react'

import type { AdminMeResponse } from '@gto/api-client/admin/react-query'
import { ArrowUpTrayIcon } from '@gto/icons-react/outline/arrow-up-tray'
import { EnvelopeIcon } from '@gto/icons-react/outline/envelope'
import { MegaphoneIcon } from '@gto/icons-react/outline/megaphone'
import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import { SparklesIcon } from '@gto/icons-react/outline/sparkles'

import { ADMIN_PERMISSIONS, type AdminPermission, hasPerm } from './me'

export type AdminSectionId =
  | 'plo-solution-upload'
  | 'announcements'
  | 'insider'
  | 'analyzer-insights'
  | 'brevo'

export type AdminNavItem = {
  label: string
  to:
    | '/plo-solution-upload/help'
    | '/plo-solution-upload/gamemodes'
    | '/plo-solution-upload/gametypes'
    | '/plo-solution-upload/upload-jobs'
    | '/announcements'
    | '/announcements/help'
    | '/announcements/new'
    | '/announcements/logs'
    | '/insider/members'
    | '/insider/features'
    | '/insider/help'
    | '/analyzer-insights'
    | '/analyzer-insights/help'
    | '/analyzer-insights/quota'
    | '/analyzer-insights/rooms'
    | '/analyzer-insights/users'
    | '/analyzer-insights/spots'
    | '/brevo/help'
    | '/brevo/events'
    | '/brevo/delivery-log'
    | '/brevo/revenue'
    | '/brevo/insights'
    | '/brevo/contacts'
    | '/brevo/automations'
    | '/brevo/settings'
  requiresManage?: true
}

export type AdminHomeCard = {
  to:
    | '/plo-solution-upload/help'
    | '/announcements/help'
    | '/insider/members'
    | '/analyzer-insights/help'
    | '/brevo/help'
  dataTst: string
  Icon: ComponentType<{ className?: string }>
  description: string
}

export type AdminSection = {
  id: AdminSectionId
  heading: string
  basePath: string
  navItems: readonly AdminNavItem[]
  homeCard: AdminHomeCard
  viewPerm: AdminPermission
  managePerm: AdminPermission
}

export const ADMIN_SECTIONS_BY_ID = {
  'plo-solution-upload': {
    id: 'plo-solution-upload',
    heading: 'PLO solution upload',
    basePath: '/plo-solution-upload',
    navItems: [
      { label: 'How to', to: '/plo-solution-upload/help' },
      { label: 'Game modes', to: '/plo-solution-upload/gamemodes' },
      { label: 'Game types', to: '/plo-solution-upload/gametypes' },
      { label: 'Upload jobs', to: '/plo-solution-upload/upload-jobs' },
    ],
    homeCard: {
      to: '/plo-solution-upload/help',
      dataTst: 'admin-home-card-plo-solution-upload',
      Icon: ArrowUpTrayIcon,
      description: 'Upload Monker solution zips, manage game modes and game types.',
    },
    viewPerm: ADMIN_PERMISSIONS.viewMonkerUploads,
    managePerm: ADMIN_PERMISSIONS.manageMonkerUploads,
  },
  announcements: {
    id: 'announcements',
    heading: 'Announcements',
    basePath: '/announcements',
    navItems: [
      { label: 'How to', to: '/announcements/help' },
      { label: 'All announcements', to: '/announcements' },
      { label: 'New announcement', to: '/announcements/new', requiresManage: true },
      { label: 'Change log', to: '/announcements/logs' },
    ],
    homeCard: {
      to: '/announcements/help',
      dataTst: 'admin-home-card-announcements',
      Icon: MegaphoneIcon,
      description:
        'Create, schedule and pause the toasters, strip banners and dashboard banners the app shows.',
    },
    viewPerm: ADMIN_PERMISSIONS.viewAnnouncements,
    managePerm: ADMIN_PERMISSIONS.manageAnnouncements,
  },
  insider: {
    id: 'insider',
    heading: 'Insider',
    basePath: '/insider',
    navItems: [
      { label: 'How to', to: '/insider/help' },
      { label: 'Members', to: '/insider/members' },
      { label: 'Features', to: '/insider/features' },
    ],
    homeCard: {
      to: '/insider/members',
      dataTst: 'admin-home-card-insider',
      Icon: SparklesIcon,
      description:
        'Review Insider signups and memberships, and manage feature exposure and account-level access records.',
    },
    viewPerm: ADMIN_PERMISSIONS.viewInsider,
    managePerm: ADMIN_PERMISSIONS.manageInsider,
  },
  'analyzer-insights': {
    id: 'analyzer-insights',
    heading: 'Analyzer insights',
    basePath: '/analyzer-insights',
    navItems: [
      { label: 'How to', to: '/analyzer-insights/help' },
      { label: 'Volume and trend', to: '/analyzer-insights' },
      { label: 'Quota pressure', to: '/analyzer-insights/quota' },
      { label: 'Rooms and rake', to: '/analyzer-insights/rooms' },
      { label: 'Per-user distribution', to: '/analyzer-insights/users' },
      { label: 'Spot frequency', to: '/analyzer-insights/spots' },
    ],
    homeCard: {
      to: '/analyzer-insights/help',
      dataTst: 'admin-home-card-analyzer-insights',
      Icon: PresentationChartLineIcon,
      description:
        'Read hand-history volume, quota pressure, poker rooms, per-user distribution and spot frequency for the last 24 months.',
    },
    viewPerm: ADMIN_PERMISSIONS.viewAnalyzerInsights,
    managePerm: ADMIN_PERMISSIONS.manageAnalyzerInsights,
  },
  brevo: {
    id: 'brevo',
    heading: 'Brevo',
    basePath: '/brevo',
    navItems: [
      { label: 'How to', to: '/brevo/help' },
      { label: 'Events', to: '/brevo/events' },
      { label: 'Delivery log', to: '/brevo/delivery-log' },
      { label: 'Revenue', to: '/brevo/revenue' },
      { label: 'Insights', to: '/brevo/insights' },
      { label: 'Contacts', to: '/brevo/contacts' },
      { label: 'Automations', to: '/brevo/automations' },
      { label: 'Settings', to: '/brevo/settings' },
    ],
    homeCard: {
      to: '/brevo/help',
      dataTst: 'admin-home-card-brevo',
      Icon: EnvelopeIcon,
      description:
        'Manage behavioural email events, delivery, revenue attribution, automations and send settings.',
    },
    viewPerm: ADMIN_PERMISSIONS.viewBrevo,
    managePerm: ADMIN_PERMISSIONS.manageBrevo,
  },
} satisfies { [Id in AdminSectionId]: AdminSection & { id: Id } }

export const ADMIN_SECTIONS: readonly AdminSection[] = Object.values(ADMIN_SECTIONS_BY_ID)

export const isUnderPath = (pathname: string, basePath: string): boolean =>
  pathname === basePath || pathname.startsWith(`${basePath}/`)

export const sectionForPath = (pathname: string): AdminSection | undefined =>
  ADMIN_SECTIONS.find((section) => isUnderPath(pathname, section.basePath))

export const canViewSection = (me: AdminMeResponse, section: AdminSection): boolean =>
  hasPerm(me, section.viewPerm)

export const canManageSection = (me: AdminMeResponse, section: AdminSection): boolean =>
  canViewSection(me, section) && hasPerm(me, section.managePerm)

export const visibleNavItems = (
  me: AdminMeResponse,
  section: AdminSection,
): readonly AdminNavItem[] =>
  section.navItems.filter((item) => item.requiresManage !== true || canManageSection(me, section))
